import StorageService from '@/services/db';
import {clearAll, setCurrentUser, setUser} from './slice';
import {AsyncStorageKeyEnum, baseSplitApi, } from '@/services';
import {ProfileTypeEnum} from '../constants';
import {LoginCredentials, OTPCredentials, OTPResponse} from '../types';
import { APIResponse, Store, User } from '@/interfaces';

interface SendOTPCredentials {
  profileType: ProfileTypeEnum;
  countryCode?: string;
  nationalNumber?: string;
  email?: string;
}

interface VerifyOTPCredentials extends SendOTPCredentials {
  otpId: string;
  otp: string;
}

export const authApi = baseSplitApi.injectEndpoints({
  endpoints: build => ({
    sendLogInOTP: build.mutation<APIResponse<OTPResponse>, OTPCredentials>({
      query: body => ({
        url: '/v1/auth/send-otp',
        method: 'POST',
        body,
      }),
    }),
    sendOTP: build.mutation<APIResponse<OTPResponse>, SendOTPCredentials>({
      query: body => ({
        url: '/v1/otp/send',
        method: 'POST',
        body,
      }),
    }),
    verifyOTP: build.mutation<APIResponse<OTPResponse>, VerifyOTPCredentials>({
      query: body => ({
        url: '/v1/otp/verify',
        method: 'POST',
        body,
      }),
    }),
    login: build.mutation<APIResponse<User>, LoginCredentials>({
      query: body => ({
        url: '/v1/auth/log-in',
        method: 'POST',
        body,
      }),
      async onQueryStarted(_credentials, {dispatch, queryFulfilled}) {
        try {
          const {data: response} = await queryFulfilled;
          dispatch(setUser({...response?.data}));
          dispatch(
            setCurrentUser({
              ...response?.data,
              profileType: ProfileTypeEnum.User,
            }),
          );
          await StorageService.setData(AsyncStorageKeyEnum.CurrentUser, {
            ...response.data,
            profileType: ProfileTypeEnum.User,
          });
        } catch (error) {
          console.error('Login failed: ', error);
          dispatch(clearAll());
          await StorageService.clearAllData();
        }
      },
    }),
    logout: build.mutation({
      query: () => ({
        url: '/v1/auth/log-out',
        method: 'POST',
        body: {},
      }),
      async onQueryStarted(credentials, {dispatch, queryFulfilled}) {
        try {
          const response = await queryFulfilled;
          dispatch(clearAll());
          await StorageService.clearAllData();
          dispatch(baseSplitApi.util.resetApiState());
        } catch (error) {
          console.error('Log-Out failed: ', error);
        }
      },
      // todo: Need to add remaining invalidatesTags here, and then remove dispatch(baseSplitApi.util.resetApiState());
      // invalidatesTags: [
      //   'Notifications',
      //   'NotificationCount',
      //   'ProductList',
      //   'Product',
      //   'OrderList',
      //   'CustomerList',
      //   'LowStockCount',
      //   'DailySales',
      //   'OrderCount',
      //   'PaymentMethodList',
      //   'ExpenseList',
      //   'ExpenseServiceDetails',
      //   'StoreProfile',
      //   'ExpenseOrder',
      //   'CategoryList',
      //   'StockValue'
      // ],
      transformResponse: (response: {data: User}, meta, arg) => response.data,
    }),
    // The mutation accepts a `Partial<Post>` arg, and returns a `Post`
    register: build.mutation<OTPResponse, Partial<User>>({
      query: (body: Partial<User>) => ({
        url: `/v1/auth/register`,
        method: 'Post',
        body,
      }),
      transformResponse: (response: {data: OTPResponse}, meta, arg) =>
        response.data,
    }),
    changeProfile: build.mutation<
      APIResponse<Store>,
      {storeId: string; self: boolean}
    >({
      query: body => ({
        url: `/v1/auth/change-profile`,
        method: 'POST',
        body,
      }),
      async onQueryStarted(_credentials, {dispatch, queryFulfilled}) {
        try {
          const {data: response} = await queryFulfilled;
          dispatch(
            setCurrentUser({
              ...response.data,
              profileType: ProfileTypeEnum.Store,
            }),
          );
          await StorageService.setData(AsyncStorageKeyEnum.CurrentUser, {
            ...response.data,
            profileType: ProfileTypeEnum.Store,
          });
          dispatch(baseSplitApi.util.resetApiState());
        } catch (error) {
          console.error('Change Profile failed: ', error);
        }
      },
      // todo: Need to add remaining invalidatesTags here, and then remove dispatch(baseSplitApi.util.resetApiState());
      // invalidatesTags: [
      //   'Notifications',
      //   'NotificationCount',
      //   'ProductList',
      //   'Product',
      //   'OrderList',
      //   'CustomerList',
      //   'LowStockCount',
      //   'DailySales',
      //   'OrderCount',
      //   'PaymentMethodList',
      //   'ExpenseList',
      //   'ExpenseServiceDetails',
      //   'StoreProfile',
      //   'ExpenseOrder',
      //   'CategoryList',
      //   'StockValue'
      // ],
    }),
  }),
  overrideExisting: false,
});
export const {
  useRegisterMutation,
  useSendLogInOTPMutation,
  useSendOTPMutation,
  useVerifyOTPMutation,
  useLoginMutation,
  useLogoutMutation,
  useChangeProfileMutation,
} = authApi;
